import React, { FC, useMemo, useRef, useState } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FormInput, registerObject } from "go-form";
import ImageColorForm from "go-app/components/ImageForm/ImageColorForm";
import { useWindowSize } from "go-core/components/useWindowSize";
import FormMoneyInput from "go-form/components/FormMoneyInput";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { CustomValidationConfig, CustomValidationConstraint, CustomValidationError } from "go-form/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { ProductApi, ProductCategoryApi, TaxApi } from "../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../services/Api/api";
import { measureTypeOptions } from "../../utils";
import CreateProductCategoryModalForm from "./CreateProductCategoryModalForm";
import ProductSummaryCard from "./ProductSummaryCard";

interface Props {
	form: UseFormReturn<ProductApi>;
	product?: ProductApi;
	setErrors: React.Dispatch<React.SetStateAction<CustomValidationError<ProductApi>[]>>;
}

type ProductCategory = Pick<ProductCategoryApi, "name"> & Partial<ProductCategoryApi>;

const ProductBasicInfoForm: FC<Props> = ({ form, product, setErrors }) => {
	const {
		formState: { errors },
		register,
		watch,
		control,
		setValue,
	} = form;
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const currency = organization?.currency ?? "PLN";
	const isPackage = watch("type") === "PACKAGE";
	const isProduct = watch("type") === "BASIC";
	const [showCategoryModal, setShowCategoryModal] = useState<boolean>(false);
	const [newCategory, setNewCategory] = useState<ProductCategory>();
	const [category, setCategory] = useState<ProductCategoryApi | undefined>(product?.category);
	const newAvatar = useRef<undefined | string>(product?.image_link?.default_link);
	const { isMobile } = useWindowSize();

	const searchCategory = async (t: string, obj: Record<string, any>, options?: Record<string, any>) => {
		return api.organization().getProductCategorySearchSelect(t, obj, {
			cancelToken: options?.token,
		});
	};

	const searchTax = async (t: string, obj: Record<string, any>, options?: Record<string, any>) => {
		return api.organization().getTaxSearchSelect(t, obj, {
			cancelToken: options?.token,
		});
	};

	const mappedMeasureTypeOptions = measureTypeOptions.map((type) => ({
		value: type,
		label: t(`enums.products.measure_types.${type}`),
	}));

	const onChangeTax = async (tax: TaxApi) => {
		registerObject(register, `tax`, ["amount", "name", "code", "id"]);
		setValue(`tax`, tax);
		setValue(`tax_id`, tax.id, { shouldDirty: true });
	};

	const handleCreateCategory = (value: string) => {
		setNewCategory({ name: value });
		setShowCategoryModal(true);
	};

	const handleSaveCategory = (category: ProductCategoryApi) => {
		setValue("category_id", category.id);
		setCategory(category);
		setShowCategoryModal(false);
	};

	const handleImageSave = (imageBase?: string) => {
		newAvatar.current = imageBase;
	};

	const nameValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
			utils: { watch },
		}),
		[setErrors, watch]
	) satisfies CustomValidationConfig;

	const categoryValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	const measureTypeValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	const taxValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	return (
		<fieldset className="form-group">
			<div className="row">
				<div className={`col-xl-${product?.id ? "7" : "9"}`}>
					<div className="row">
						<div className="col-md-6">
							<FormInput
								name="name"
								errors={errors}
								register={register}
								label={t("lib:common.word.name")}
								customValidationConfig={nameValidationConfig}
							/>
							<FormInput
								type="hidden"
								errors={errors}
								register={register}
								name="type"
								defaultValue={product?.type}
							/>
						</div>
						<div className="col-md-6">
							<FormSelectGroup
								errors={errors}
								control={control}
								getOptionValue={(opt) => opt.id}
								loadOptions={searchCategory}
								toEditPath
								name="category_id"
								defaultValue={{
									label: category?.name,
									id: category?.id,
								}}
								label={t("common.word.category")}
								onCreateOption={handleCreateCategory}
								path={`/${organization.id}/products/categories/`}
								customValidationConfig={categoryValidationConfig}
								data-testid="category_id"
							/>
							{showCategoryModal && (
								<CreateProductCategoryModalForm
									isShown={showCategoryModal}
									value={newCategory?.name || ""}
									onHide={() => setShowCategoryModal(false)}
									onSave={(data) => handleSaveCategory(data)}
								/>
							)}
						</div>
					</div>
					<div className="row">
						<div className="col-md-6">
							<FormInput
								name="sku"
								errors={errors}
								register={register}
								label={t("modules.product.field.sku.title")}
							/>
						</div>
						<div className="col-md-6">
							<FormSelectGroup
								errors={errors}
								control={control}
								options={mappedMeasureTypeOptions}
								name="measure_type"
								isClearable={false}
								label={t("common.word.measure_type")}
								customValidationConfig={measureTypeValidationConfig}
								data-testid="measure_type"
							/>
						</div>
					</div>
					<div className="row">
						{!isPackage && (
							<div className={`col-md-6 ${isProduct ? "mb-3" : ""}`}>
								<FormSelectGroup
									errors={errors}
									control={control}
									loadOptions={searchTax}
									name="tax_id"
									onChange={(value, obj) => onChangeTax(obj)}
									isClearable={false}
									defaultValue={{
										label: product?.tax?.name,
										value: product?.tax?.id,
									}}
									getOptionValue={(option) => option.id || option.value}
									label={t("modules.product.field.tax.title")}
									customValidationConfig={taxValidationConfig}
									data-testid="tax_id"
								/>
							</div>
						)}
						{isProduct && (
							<div className={`col-md-6 ${isProduct ? "mb-3" : ""}`}>
								<Form.Label>{t("modules.product.field.default_price_net_money.title")}</Form.Label>
								<InputGroup className="form-input-group">
									<FormMoneyInput
										errors={errors}
										control={control}
										name="default_price_net_money.amount"
										currency=""
										decimalScale={3}
									/>
									<InputGroup.Text style={{ height: "31px" }}>{currency}</InputGroup.Text>
								</InputGroup>
								<Form.Text style={{ marginTop: "-7px" }} muted>
									{t("modules.product.field.default_price_net_money.help_text")}
								</Form.Text>
							</div>
						)}
						{!isPackage && (
							<>
								<div className="col-md-6">
									<FormNumberInput
										name="critical_amount_level"
										errors={errors}
										control={control}
										suffix=""
										defaultValue={product?.critical_amount_level}
										label={t("modules.product.field.critical_amount_level.title")}
									/>
								</div>
								<div className="col-md-6">
									<FormNumberInput
										name="optimal_amount_level"
										errors={errors}
										control={control}
										suffix=""
										defaultValue={product?.optimal_amount_level}
										label={t("modules.product.field.optimal_amount_level.title")}
									/>
								</div>
								<div className="col-md-6">
									<FormNumberInput
										name="default_volume"
										errors={errors}
										control={control}
										suffix=""
										decimalScale={5}
										label={t("common.word.default_volume")}
										help={t("modules.product.field.default_volume.help_text")}
									/>
								</div>
							</>
						)}
					</div>
				</div>
				<div className={`col-xl-${product?.id ? "5" : "3"}`}>
					<div className="row item-summary-and-image-container">
						{product?.id && (
							<div className="col-xl-5 item-summary-col">
								<ProductSummaryCard product={product} />
							</div>
						)}
						<div className={`col-xl-7 ${isMobile ? "w-auto" : ""}`}>
							<ImageColorForm
								form={form}
								handleSave={handleImageSave}
								prefix="image"
								mode="IMAGE"
								placeholder="/graphics/user-avatar.svg"
								data={product}
								resourceLink={product?.image_link || watch("image_link")}
								resourceData={newAvatar.current}
							/>
						</div>
					</div>
				</div>
			</div>
		</fieldset>
	);
};

export default ProductBasicInfoForm;
